import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { formatCurrency, formatDate } from "../utils/utils";
import ConfirmDialog from "../components/confirm-alert/ConfirmDialog ";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsers,
  addUser,
  deleteUser,
  updateUser,
  changeUserStatus,
  findUserByName,
} from "../redux/reducers/userSlice";
import { adminResendCredentials } from "../redux/reducers/trialSlice";
import Loader from "../components/loader/loader";

const UserManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
  });

  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.users);
  const [filteredUsers, setUsers] = useState(users ?? []);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    filterUsers(filter);
  }, [users, filter]);

  const filterUsers = (filter) => {
    if (filter === "All") {
      setUsers(users);
    } else if (filter === "Paid") {
      const activeUsers = users?.filter(
        (user) => user.paymentStatus === "Paid"
      );
      setUsers(activeUsers);
    } else if (filter === "Unpaid") {
      const inactiveUsers = users?.filter(
        (user) => user.paymentStatus === "Unpaid"
      );
      setUsers(inactiveUsers);
    } else if (filter === "Trial") {
      const inactiveUsers = users?.filter(
        (user) => user.paymentStatus === "Trial"
      );
      setUsers(inactiveUsers);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setEditMode(false);
    setCurrentUser(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    dispatch(deleteUser(id));
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const handleSearch = (e) => {
    dispatch(findUserByName(e.target.value));
    if (e.target.value === "") {
      dispatch(getUsers());
    }
  };

  const handleAction = (actionType, id) => {
    const user = filteredUsers.find((user) => user._id === id);

    if (actionType === "Edit") {
      setCurrentUser(user);
      setEditMode(true);
      setIsModalOpen(true);
    } else if (actionType === "Delete") {
      setConfirmDialog({
        isOpen: true,
        title: "Confirm Delete",
        message: "Are you sure you want to delete this user?",
        onConfirm: () => handleDelete(id),
        onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
      });
    } else if (actionType === "Block/Unblock") {
      setConfirmDialog({
        isOpen: true,
        title: "Confirm Action",
        message: `Are you sure you want to ${
          user.paymentStatus === "Unpaid" ? "unblock" : "block"
        } this user?`,
        onConfirm: () => handleBlockUnblock(user),
        onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
      });
    } else if (actionType === "Confirm Payment") {
      setConfirmDialog({
        isOpen: true,
        title: "Confirm Payment",
        message: "Are you sure you want to mark the payment as confirmed?",
        // onConfirm: () => handleConfirmPayment(id),
        onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
      });
    }
  };

  const saveUser = (userData) => {
    const userDataWithId = { id: userData?._id, updates: userData };

    editMode
      ? dispatch(updateUser(userDataWithId))
      : dispatch(addUser(userData));

    closeModal();
  };

  const handleBlockUnblock = (userData) => {
    const updatedData = {
      id: userData._id,
      status: userData?.status === "Active" ? "Inactive" : "Active",
    };
    dispatch(changeUserStatus(updatedData));
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const handleResendCredentials = async (userId) => {
    try {
      await dispatch(adminResendCredentials(userId)).unwrap();
      toast.success("Credentials resent successfully");
    } catch (err) {
      toast.error(err || "Failed to resend credentials");
    }
  };

  return (
    <>
      <AdminLayout>
        <div className="p-2">
          <Toaster position="top-right" />
          <Loader loading={loading} />
          <Header
            title="User Management"
            description="Manage users and permissions"
            subTitle="User Management"
            buttonText={"Add User"}
            btnOnlcick={openModal}
          >
            <>
              <input
                type="text"
                placeholder="Search"
                onChange={(e) => {
                  handleSearch(e);
                }}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2 w-full md:w-auto"
              />
              <select
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 py-2 px-5 w-full md:w-auto mt-2 md:mt-0"
              >
                <option value={"All"}>All User</option>
                <option value={"Paid"}>Paid</option>
                <option value={"Unpaid"}>Unpaid</option>
                <option value={"Trial"}>Trial</option>
              </select>
            </>
          </Header>
          <div className="overflow-x-auto mt-5">
            {users?.length === 0 ? (
              <>
                <div className="flex justify-center items-center h-96">
                  <h2 className="text-2xl text-gray-400">No users found</h2>
                </div>
              </>
            ) : (
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Name
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Email
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Payment Status
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Date Joining
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      License Fee
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Last Payment Date
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-sm border font-bold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers?.map((row) => (
                    <tr key={row.id} className="border-b">
                      <td className="px-6 py-4 text-sm text-gray-700 border">
                        {row?.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 border">
                        {row?.email}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          row?.paymentStatus === "Paid"
                            ? "text-green-600"
                            : row?.paymentStatus === "Trial"
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {row?.paymentStatus}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 border">
                        {formatDate(row?.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 border">
                        {row?.paymentStatus === "Trial"
                          ? <span className="text-yellow-600 font-medium">Free Trial</span>
                          : formatCurrency(row?.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 border">
                        {row?.lastPaymentDate
                          ? formatDate(row?.lastPaymentDate)
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm w-44 text-center border">
                        <div
                          className={`px-2 py-1 rounded w-full ${
                            row?.status === "Inactive"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {row?.status}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm flex flex-col md:flex-row gap-2">
                        <button
                          onClick={() => handleAction("Edit", row._id)}
                          className="text-blue-600 hover:bg-green-200 border p-2 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleAction("Delete", row._id)}
                          className="text-red-600 hover:bg-red-200 border p-2 rounded"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => handleAction("Block/Unblock", row._id)}
                          className={`${
                            row.status === "Active"
                              ? "text-red-600 hover:bg-red-200"
                              : "text-green-600 hover:bg-green-200"
                          } border p-2 rounded w-20`}
                        >
                          {row?.status === "Active" ? "Block" : "Un-Block"}
                        </button>
                        {row.paymentStatus === "Trial" && (
                          <button
                            onClick={() => handleResendCredentials(row._id)}
                            className="text-purple-600 hover:bg-purple-100 border p-2 rounded whitespace-nowrap"
                            title="Resend login credentials to this user"
                          >
                            Resend Email
                          </button>
                        )}
                        {/* <button
                        onClick={() => handleAction("Confirm Payment", row._id)}
                        className="text-green-600 hover:bg-green-200 border p-2 rounded"
                      >
                        Confirm Payment
                      </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {isModalOpen && (
          <AddUserModal
            isOpen={isModalOpen}
            closeModal={closeModal}
            addUser={saveUser}
            currentUser={currentUser}
            editMode={editMode}
          />
        )}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      </AdminLayout>
    </>
  );
};

// export default UserManagement;


const AddUserModal = ({
  isOpen,
  closeModal,
  addUser,
  currentUser,
  editMode,
}) => {
  const [user, setUser] = useState(
    currentUser || {
      name: "",
      email: "",
      password: "", // Add password field
      confirmPassword: "", // Add confirm password field
      amount: 0,
    }
  );
  const [passType, setPassType] = useState(true);
  const [confirmPassType, setConfirmPassType] = useState(true);


  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!user.name || !user.email || (!editMode && !user.password)) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate password match if adding new user
    if (!editMode && user.password !== user.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    addUser(user);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center w-full">
      <div className="bg-white rounded-lg p-10 w-full md:w-1/2 mx-2">
        <h2 className="text-lg font-semibold mb-4">
          {editMode ? "Edit User" : "Add User"}
        </h2>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col md:flex-row w-full gap-2">
            <div className="w-full md:w-1/2">
              <label className="block mb-2 text-sm text-gray-500">
                Client Name:
              </label>
              <input
                type="text"
                name="name"
                placeholder="Client Name"
                value={user.name}
                onChange={handleChange}
                className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
              />
            </div>
            <div className="w-full md:w-1/2">
              <label className="block mb-2 text-sm text-gray-500">
                Client Email:
              </label>
              <input
                type="email"
                name="email"
                placeholder="Client Email"
                value={user.email}
                onChange={handleChange}
                className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
              />
            </div>
          </div>

          {!editMode && (
            <div className="flex flex-col md:flex-row w-full gap-2">
              <div className="w-full md:w-1/2 relative">
                <label className="block mb-2 text-sm text-gray-500">
                  Password
                </label>
                <input
                  type={`${passType ? "password" : "text"}`}
                  name="password"
                  placeholder="Password"
                  value={user.password}
                  onChange={handleChange}
                  className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
                />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setPassType(!passType);
                  }}
                  className="absolute right-2 top-9 p-1"
                >
                  {passType ? "👁️" : "🚫"}
                </button>
              </div>
              <div className="w-full md:w-1/2 relative">
                <label className="block mb-2 text-sm text-gray-500">
                  Confirm Password
                </label>
                <input
                  type={`${confirmPassType ? "password" : "text"}`}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={user.confirmPassword}
                  onChange={handleChange}
                  className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
                />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setConfirmPassType(!confirmPassType);
                  }}
                  className="absolute right-2 top-9 p-1"
                >
                  {confirmPassType ? "👁️" : "🚫"}
                </button>
              </div>
            </div>
          )}
          {!editMode && (
            <div className="flex w-full items-center justify-between">
              {user.password === user.confirmPassword ? (
                <p className="text-green-500">✅ Passwords match</p>
              ) : (
                <p className="text-red-500">🚫 Passwords do not match</p>
              )}
            </div>
          )}
          <div className="flex flex-col md:flex-row w-full gap-2">
            <div className="w-full md:w-1/2">
              <label className="block mb-2 text-sm text-gray-500">
                License Fee:
              </label>
              <input
                type="number"
                name="amount"
                placeholder="Amount Paid"
                value={user.amount}
                onChange={handleChange}
                className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
              />
            </div>
            {editMode && (
              <div className="w-full md:w-1/2">
                <label className="block mb-2 text-sm text-gray-500">
                  Payment Status:
                </label>
                <select
                  name="paymentStatus"
                  value={user.paymentStatus}
                  onChange={handleChange}
                  className="w-full p-2 border-2 border-gray-500 rounded  active:outline-purple-200 focus:outline-purple-500"
                >
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Trial">Trial</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-300 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            {editMode ? "Save Changes" : "Add User"}
          </button>
        </div>
      </div>
    </div>
  );
};
export default UserManagement;
